import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { DiscordLogo, TwitterLogo, YoutubeLogo, Globe } from "@phosphor-icons/react";

// Iter 28 — Public directory of people behind BEYX.PL. Read-only view.
// Admins manage the list under /admin -> Creators tab.
export default function Creators() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/creators")
      .then((r) => setCreators(r.data))
      .catch(() => setCreators([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="creators-page">
      <div className="border-b border-white/10 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
          // {t.creatorsEyebrow}
        </div>
        <h1 className="font-display font-black uppercase italic text-4xl sm:text-5xl lg:text-6xl mt-2">
          {t.creatorsTitle}
        </h1>
        <p className="text-white/60 max-w-2xl mt-3 font-mono text-sm">{t.creatorsSubtitle}</p>
      </div>

      {loading ? (
        <div className="text-white/40 font-mono text-sm py-16 text-center">{t.loading}</div>
      ) : creators.length === 0 ? (
        <div className="text-white/40 font-mono text-sm py-16 text-center" data-testid="creators-empty">
          {t.noCreatorsYet}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {creators.map((c) => (
            <CreatorCard key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreatorCard({ c }) {
  const socials = [
    c.discord_url && { url: c.discord_url, Icon: DiscordLogo, color: "#5865F2", label: "Discord" },
    c.twitter_url && { url: c.twitter_url, Icon: TwitterLogo, color: "#1DA1F2", label: "Twitter" },
    c.youtube_url && { url: c.youtube_url, Icon: YoutubeLogo, color: "#FF0000", label: "YouTube" },
    c.website_url && { url: c.website_url, Icon: Globe, color: "#00E5FF", label: "Website" },
  ].filter(Boolean);
  return (
    <div
      data-testid={`creator-card-${c.id}`}
      className="relative group border border-white/10 bg-[#0C0C14]/80 backdrop-blur-sm hover:border-[#FF0055]/60 transition-all overflow-hidden"
    >
      <div className="aspect-[4/3] bg-black/40 relative overflow-hidden">
        {c.avatar_url ? (
          <img
            src={c.avatar_url}
            alt={c.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 font-display font-black italic text-6xl">
            {c.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C14] via-transparent to-transparent pointer-events-none" />
      </div>
      <div className="p-5 space-y-2">
        <div>
          <h3 className="font-display font-black italic uppercase text-xl truncate" data-testid={`creator-card-${c.id}-name`}>
            {c.name}
          </h3>
          {c.role && (
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#00E5FF] font-mono mt-0.5">
              {c.role}
            </div>
          )}
        </div>
        {c.bio && (
          <p className="text-white/70 text-sm leading-relaxed" data-testid={`creator-card-${c.id}-bio`}>
            {c.bio}
          </p>
        )}
        {socials.length > 0 && (
          <div className="flex items-center gap-2 pt-3 border-t border-white/5">
            {socials.map(({ url, Icon, color, label }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noreferrer"
                title={label}
                className="w-8 h-8 border border-white/10 flex items-center justify-center text-white/60 transition-colors hover:border-transparent"
                onMouseEnter={(e) => { e.currentTarget.style.color = color; e.currentTarget.style.borderColor = `${color}80`; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = ""; e.currentTarget.style.borderColor = ""; }}
              >
                <Icon size={14} weight="fill" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
